/* eslint-disable */

import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, CircularProgress, List, ListItem, ListItemText, TextField, InputAdornment, ListItemIcon } from '@material-ui/core';
import envConfig from '../../../config/environment-config';
import { useForm } from 'react-hook-form';
import { IPharmaceuticalCrendentials, LocalStorageKeysEnum } from '../common/contracts';
import { useAlert } from '../../../contexts/pharmaceutical-portal/Alert.context';
import { localStorageMethods } from '../common/helpers';
import { discountsService } from '../../../services';
import { Autocomplete } from '@material-ui/lab';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import SearchIcon from '@material-ui/icons/Search';
import DiscountCard from './components/DiscountCard';

interface DiscountAndBoxes {
  boxesTo:  number | null;
  boxesFrom:  number | null;
  discountPercentage: number;
  isBiggestDiscount: boolean;
}

interface Discount {
  name: string;
  presentation: string;
  discountType: string;
  discountValue: number;
  biggestDiscount?: DiscountAndBoxes;
  otherDiscounts?: DiscountAndBoxes[];
}

const DiscountsPage: React.FC = () => {
  const [isLoadingDefaultValue, setIsLoadingDefaultValue] = useState<boolean>(true);
  const [callApi, setCallApi] = useState<boolean>(false);
  const { showAlert, hideAlert } = useAlert();
  const pharmaceuticalCredentials = localStorageMethods.getItem<IPharmaceuticalCrendentials>(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS);

  const {
    setValue,
    watch,
  } = useForm();

  const watchData = watch() as {
    uf: string;
    lineId: string;
  };

  const handleSearch = (param: any) => {
    if(param.id) {
      handleProductClick(param.id);
    }
  };

  const { data: productsData, isLoading: isLoadingProducts } = discountsService.findProductsAvailableToDiscounts(
    envConfig.boehringerProgramCode,
  );

  const products = productsData?.data?.map(product => ({
    label: product.name,
    id: product.lineId.toString(),
  })) || [];

  const { data: discountsData, isLoading: isLoadingDiscounts, error: findDiscountsError } = discountsService.findDiscountsByPatient({
    programId: envConfig.boehringerProgramCode,
    familyId: watchData.lineId,
    uf: pharmaceuticalCredentials?.crfUf,
  }, callApi, setCallApi);

  const mappedDiscounts: { [key: string]: Discount } = {};
  discountsData?.data?.rules?.forEach(rule => {
    const { productDescription, ruleName, ranges } = rule;

    if (!mappedDiscounts[productDescription]) {
      mappedDiscounts[productDescription] = {
        name: discountsData?.data?.familyDescription ?? '-',
        presentation: productDescription,
        discountType: ruleName,
        discountValue: 0,
        biggestDiscount: undefined,
        otherDiscounts: []
      };
    }

    ranges.forEach(range => {
      const discountAndBoxes: DiscountAndBoxes = {
        boxesFrom: range.boxesFrom,
        boxesTo: range.boxesTo,
        discountPercentage: range.discountPercentage ?? 0,
        isBiggestDiscount: false
      };

      // Adiciona o desconto atual ao array otherDiscounts
      mappedDiscounts[productDescription].otherDiscounts!.push(discountAndBoxes);

      // Lógica para determinar o biggestDiscount
      if (
        !mappedDiscounts[productDescription].biggestDiscount ||
        discountAndBoxes.discountPercentage > mappedDiscounts[productDescription].biggestDiscount!.discountPercentage
      ) {
        mappedDiscounts[productDescription].biggestDiscount = discountAndBoxes;
        mappedDiscounts[productDescription].discountValue = discountAndBoxes.discountPercentage;
      }
    });
  });

  // Pós-processamento: ordenar otherDiscounts e marcar o isBiggestDiscount
  for (const productDescription in mappedDiscounts) {
    if (mappedDiscounts.hasOwnProperty(productDescription)) {
      const currentProduct = mappedDiscounts[productDescription];

      if (currentProduct.otherDiscounts && currentProduct.otherDiscounts.length > 0) {
        // Encontrar o maior desconto no array
        let maxDiscountPercentage = -1;
        currentProduct.otherDiscounts.forEach(discount => {
          if (discount.discountPercentage > maxDiscountPercentage) {
            maxDiscountPercentage = discount.discountPercentage;
          }
        });

        // Marcar o isBiggestDiscount para true
        currentProduct.otherDiscounts.forEach(discount => {
          if (discount.discountPercentage === maxDiscountPercentage) {
            discount.isBiggestDiscount = true;
          }
        });

        // Ordenar otherDiscounts em ordem decrescente de discountPercentage
        currentProduct.otherDiscounts.sort((a, b) => b.discountPercentage - a.discountPercentage);

        // Garantir que biggestDiscount aponte para a mesma instância marcada
        // Isso é importante para que as mudanças em 'isBiggestDiscount' persistam
        currentProduct.biggestDiscount = currentProduct.otherDiscounts[0];
        currentProduct.discountValue = currentProduct.biggestDiscount.discountPercentage;
      }
    }
  }

  const productsDiscounts: Discount[] = Object.values(mappedDiscounts);

  useEffect(() => {
    if(isLoadingProducts || isLoadingDiscounts) {
      setIsLoadingDefaultValue(true);
    } else {
      setIsLoadingDefaultValue(false);
    }

    if (findDiscountsError) {
      showAlert(
        'Descontos',
        'Ops! Tivemos um problema ao obter os descontos, tente novamente mais tarde.',
        'error',
      );
      setTimeout(() => hideAlert(), 3000);
    }
  }, [isLoadingProducts, isLoadingDiscounts, findDiscountsError]);


  const handleProductClick = (lineId: string) => {
    setValue('lineId', lineId);
    setCallApi(true);
  }

  if (isLoadingDefaultValue) {
    return (
      <div style={{ margin: '200px auto', marginLeft: '48%' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container>
      <Box style={{ flexGrow: 1, padding: '10px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Typography variant="h5" style={{ fontWeight: 'bold', fontSize: '1.8rem', textAlign: 'left', width: '100%' }}>
            Descontos
          </Typography>
        </Box>
        <hr style={{ border: '1px solid lightgray', margin: '16px 0 36px 0', width: '100%' }} />

        <Box mb={4}>
          <Autocomplete
            fullWidth
            placeholder="Buscar Medicamento"
            disablePortal
            options={products}
            getOptionLabel={(product) => product.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar Medicamento"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            onChange={(event, newInputValue) => {
              handleSearch(newInputValue);
            }}
          />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={3}>
            <List>
              {products.length > 0 ? products.map((prod) => (
                <ListItem
                  selected={prod.id === watchData.lineId}
                  key={prod.id}
                  button
                  onClick={() => handleProductClick(prod.id)}
                >
                <ListItemText primary={prod.label} />
                  {prod.id === watchData.lineId &&
                    <ListItemIcon>
                      <ArrowForwardIosIcon />
                    </ListItemIcon>
                  }
                </ListItem>
              )) : 
              <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', marginTop: '40px' }}>
                <Typography variant="body1" component="p" >
                  Nenhum produto encontrado
                </Typography>
              </div>}
            </List>
          </Grid>

          <Grid item xs={12} sm={9}>
            <Typography variant="h6" component="h3" gutterBottom style={{ fontWeight: 'bold' }}>
              { productsDiscounts ? productsDiscounts[0]?.name : '-' }
            </Typography>

            {productsDiscounts.length > 0 ? productsDiscounts.map((product, index) => (
              <DiscountCard key={index} product={product} index={index} />
            )) :
            <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', marginTop: '40px' }}>
              <Typography variant="body1" component="p" >
                Não foram encontrados descontos ou o produto não foi selecionado.
              </Typography>
            </div>}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default DiscountsPage;
